import { ICalloutContentStyles, IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCallout, IPDetailsList, IPLink, IPPersona, IPStack, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import InfoMessage from 'components/Notification/InfoMessage';
import { Approval, ApprovalDetailStatus, ApprovalMainStatus } from 'models/approval';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTaskApprovalComments } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeNoSeconds } from 'utils/datetime';

interface IApprovalCallOutProps {
  approvalMain: ApprovalMainStatus | undefined;
  approvalDetails: ApprovalDetailStatus[];
  targetId: string;
  isVisible: boolean;
  onOpenHistory?: () => void;
}

export const ApprovalCallOut = (props: IApprovalCallOutProps) => {
  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      minWidth: 620,
      maxWidth: 1000,
    },
  };

  //
  // Main render
  //
  return (
    <IPCallout
      hidden={!props.isVisible}
      styles={calloutStyle}
      setInitialFocus
      gapSpace={0}
      target={`#${props.targetId}`}
      beakWidth={0}
    >
      <ApprovalCallOutContent
        hidden={!props.isVisible}
        approvalMain={props.approvalMain}
        approvalDetails={props.approvalDetails}
        onOpenHistory={props.onOpenHistory}
      />
    </IPCallout>
  );
};

//
// Content
//
interface IApprovalCallOutContentProps {
  hidden: boolean;
  approvalMain: ApprovalMainStatus | undefined;
  approvalDetails: ApprovalDetailStatus[];
  onOpenHistory?: () => void;
}

export const ApprovalCallOutContent = (props: IApprovalCallOutContentProps) => {
  const { t } = useTranslation(['translation', 'library']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [remarks, setRemarks] = useState<Record<number, string>>({});
  const [timer, setTimer] = useState<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.hidden) {
      setTimer(setTimeout(() => loadData(), 200));
    } else {
      clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hidden]);

  const loadData = async () => {
    try {
      if (!props.approvalMain || props.hidden) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const data = await apiGetTaskApprovalComments(props.approvalMain.taskId, accessToken);
      setRemarks(data);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'user',
      minWidth: 150,
      name: t('library:ApprovalTasks.Header.User'),
      isResizable: true,
      isMultiline: true,
      onRender: (item?: ApprovalDetailStatus, index?: number, column?: IColumn) => {
        if (!item) return;
        const user = appContext.globalDataCache.users.get(item?.userId);

        return <IPPersona imageUrl={user.getPhotoURLObj()} text={user.name} size={IPPersonaSize.size24} />;
      },
    },
    {
      key: 'status',
      minWidth: 120,
      name: t('library:ApprovalTasks.Header.Status'),
      onRender: (item?: ApprovalDetailStatus, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{Approval.getStatusText(item.status, t)}</IPText>;
      },
    },
    {
      key: 'date',
      minWidth: 150,
      name: t('library:ApprovalTasks.Header.Date'),
      onRender: (item?: ApprovalDetailStatus, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{toLocaleDateTimeNoSeconds(item.date)}</IPText>;
      },
    },
    {
      key: 'comment',
      minWidth: 200,
      name: t('library:ApprovalTasks.Header.Comment'),
      isMultiline: true,
      isResizable: true,
      onRender: (item?: ApprovalDetailStatus, index?: number, column?: IColumn) => {
        if (!item || isLoading) return null;
        const comment = remarks[item.taskId];
        if (!comment) return null;

        return <IPText>{comment}</IPText>;
      },
    },
  ];

  return (
    <IPStack verticalFill>
      {props.approvalMain?.templateId && (
        <InfoMessage
          message={t('library:ApprovalTasks.CreateNewTab.TemplateApplied', {
            name: appContext.globalDataCache.templates.get(props.approvalMain.templateId).name,
          })}
        />
      )}
      <IPStack.Item grow styles={{ root: { paddingLeft: 20, paddingRight: 20, paddingBottom: 20 } }}>
        <IPDetailsList
          items={props.approvalDetails || []}
          columns={columns}
          compact={true}
          layoutMode={IPDetailsListLayoutMode.fixedColumns}
          selectionMode={IPSelectionMode.none}
          selectionPreservedOnEmptyClick={true}
          isHeaderVisible={true}
        />
      </IPStack.Item>
      {props.onOpenHistory && (
        <IPStack.Item styles={{ root: { paddingLeft: 20, paddingRight: 20, paddingBottom: 10 } }}>
          <IPLink
            onClick={() => {
              if (props.onOpenHistory) props.onOpenHistory();
            }}
          >
            {t('library:ApprovalTasks.LatestApproval.HistoryLink')}
          </IPLink>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
